<script setup lang="ts">
import { useTranslation } from 'i18next-vue'
import AppointmentBranchDialog from './AppointmentBranchDialog.vue'
import AppointmentCalendar from './AppointmentCalendar.vue'
import { AppointmentBranch } from '@/type/Appointment'

interface Props {
  // Branch
  branchText: string
  showBranchPicker: boolean
  branches: AppointmentBranch[]
  hasBranch: boolean
  // Date
  dateText: string
  showCalendar: boolean
  calendarMinDate: Date | null
  calendarMaxDate: Date | null
  dateSelect: Date | null
  formatter: (day: any) => any
}

interface Emits {
  (e: 'update:showBranchPicker', value: boolean): void
  (e: 'update:showCalendar', value: boolean): void
  (e: 'confirmBranch', value: { text: string; uuid: string }): void
  (e: 'confirmCalendar', value: Date): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()
const { t } = useTranslation()
</script>

<template>
  <van-cell-group class="group-container" inset>
    <!-- Branch Selection -->
    <slot name="activator">
      <van-field
        :model-value="branchText"
        readonly
        clickable
        :label="t('appointmentrequest.branch')"
        :placeholder="t('appointmentrequest.select_branch')"
        :rules="[
          {
            required: true,
            message: t('appointmentrequest.select_branch_require'),
          },
        ]"
        @click="emit('update:showBranchPicker', true)"
      >
        <template #right-icon>
          <div class="icon-button">
            <van-icon name="arrow-down" size="24" />
          </div>
        </template>
      </van-field>
    </slot>

    <AppointmentBranchDialog
      :show="showBranchPicker"
      :branches="branches"
      @confirm="emit('confirmBranch', $event)"
      @close="emit('update:showBranchPicker', false)"
    />

    <!-- Date Selection -->
    <van-field
      v-if="hasBranch"
      :model-value="dateText"
      readonly
      clickable
      :label="t('appointmentrequest.calendar')"
      :placeholder="t('appointmentrequest.select_date')"
      @click="emit('update:showCalendar', true)"
      :rules="[
        {
          required: true,
          message: t('appointmentrequest.select_date_require'),
        },
      ]"
    >
      <template #right-icon>
        <div class="icon-button">
          <van-icon name="arrow-down" size="24" />
        </div>
      </template>
    </van-field>

    <AppointmentCalendar
      :show="showCalendar"
      @update:show="emit('update:showCalendar', $event)"
      @confirm="emit('confirmCalendar', $event)"
      :formatter="formatter"
      :calendar-min-date="calendarMinDate"
      :calendar-max-date="calendarMaxDate"
      :date-select="dateSelect"
    />
  </van-cell-group>
</template>

<style scoped>
.group-container {
  margin: 15px 0px;
  border: 2px solid #c8c9cc;
  border-radius: 20px;
}

.icon-button {
  background-color: var(--van-button-primary-background);
  color: white;
  border-radius: 100%;
}
</style>
